// hooks/useAudienceTerm.js
// ─────────────────────────────────────────────────────────────────────────────
// Returns the right learner-vocabulary for the current institute based on
// `audienceMode` exposed by GET /api/institute/site-config (which itself is
// gated by Plan.cbtEnterprise on the BE).
//
// Usage:
//
//   import useAudienceTerm from '../hooks/useAudienceTerm';
//   const t = useAudienceTerm();
//   <h1>My {t.plural}</h1>
//   <button>Add {t.singular}</button>
//
// All values are plain strings — components stay agnostic of where the
// terminology comes from. When an institute on the cbtEnterprise plan flips
// audienceMode to 'employee', every screen using this hook re-renders with
// "Employee" copy. Default (and forced default for non-enterprise plans) is
// "Student".
// ─────────────────────────────────────────────────────────────────────────────

import { useCmsSite } from '../contexts/CmsSiteContext';

const STUDENT_TERMS = Object.freeze({
  mode: 'student',
  singular: 'Student',
  plural: 'Students',
  lower: 'student',
  lowerPlural: 'students',
  loginLabel: 'Student',
  loginPageTitle: 'Student Login',
  possessive: "Student's",
  myArea: 'My Learning',
  audienceWord: 'students',
});

const EMPLOYEE_TERMS = Object.freeze({
  mode: 'employee',
  singular: 'Employee',
  plural: 'Employees',
  lower: 'employee',
  lowerPlural: 'employees',
  loginLabel: 'Employee',
  loginPageTitle: 'Employee Login',
  possessive: "Employee's",
  myArea: 'My Training',
  audienceWord: 'employees',
});

export default function useAudienceTerm() {
  const { audienceMode } = useCmsSite() || {};
  return audienceMode === 'employee' ? EMPLOYEE_TERMS : STUDENT_TERMS;
}

export { STUDENT_TERMS, EMPLOYEE_TERMS };
